// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { forwardRef } from 'react';
import { Platform } from 'react-native';
import Svg, { G, Rect, Text as SvgText } from 'react-native-svg';

import {
  CARD_WIDTH,
  FOOTER_HEIGHT,
  LINE_HEIGHT,
  MORE_HEIGHT,
  PADDING,
  ROW_GAP,
  ROW_HEIGHT,
  SECTION_GAP,
  SECTION_TITLE_HEIGHT,
  layoutShareCard,
  type ShareCardContent,
} from '@/components/shareCardLayout';
import appColors from '@/theme/colors';

export type ShareLeaderboardCardProps = ShareCardContent & {
  /** Label for a trimmed tail, already interpolated with the count. */
  moreLabel: (count: number) => string;
  /** On-screen width of the preview; the card always draws at CARD_WIDTH internally. */
  previewWidth: number;
};

const MEDAL_COLORS = [appColors.accent.gold, appColors.border.onLight, appColors.accent.bronze];

// react-native-svg has no text metrics, so anything that would overrun its column is cut at a
// width-per-character estimate. 0.52em is a fair average for a bold sans-serif face.
const CHAR_WIDTH_RATIO = 0.52;

export const ellipsize = (value: string, fontSize: number, maxWidth: number): string => {
  const maxChars = Math.max(1, Math.floor(maxWidth / (fontSize * CHAR_WIDTH_RATIO)));
  const trimmed = value.trim();
  return trimmed.length <= maxChars ? trimmed : `${trimmed.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
};

// The card is rasterized through an <img> on web, where the app's bundled fonts are not available
// to the SVG document. A generic stack keeps the export looking the same everywhere.
const FONT = Platform.OS === 'web' ? 'Helvetica, Arial, sans-serif' : 'System';

/**
 * The results picture that gets shared.
 *
 * Deliberately not a screenshot of the page: a full-page capture is 8000px+ tall, and every
 * messenger downscales by the longest edge, so it arrives unreadable. This is the same results,
 * condensed to fit — see shareCardLayout for the budget.
 *
 * Drawn from SVG primitives only, with no <Image> nodes: react-native-svg's `toDataURL`
 * rasterizes through a canvas on web, and an externally-sourced bitmap in the tree would taint
 * that canvas and make the export throw.
 */
export const ShareLeaderboardCard = forwardRef<Svg, ShareLeaderboardCardProps>(
  function ShareLeaderboardCard({ moreLabel, previewWidth, ...content }, ref) {
    const layout = layoutShareCard(content);
    const { height } = layout;
    const valueX = CARD_WIDTH - PADDING - 24;
    const nameX = PADDING + 104;
    const nameMaxWidth = valueX - nameX - 170;
    // The condensed lines put a label on the left and "winner · score" on the right; the split
    // below keeps a long game name from running into a long player name.
    const valueMaxWidth = 400;
    const labelMaxWidth = valueX - (PADDING + 24) - valueMaxWidth - 24;
    let y = PADDING;

    const mutedLine = (
      text: string,
      fontSize: number,
      anchor: 'start' | 'middle' | 'end',
      x: number,
      lineY: number
    ) => (
      <SvgText
        x={x}
        y={lineY}
        fill={appColors.text.muted}
        fontFamily={FONT}
        fontSize={fontSize}
        fontWeight="600"
        textAnchor={anchor}
      >
        {text}
      </SvgText>
    );

    return (
      <Svg
        ref={ref}
        width={previewWidth}
        height={(previewWidth * height) / CARD_WIDTH}
        viewBox={`0 0 ${CARD_WIDTH} ${height}`}
      >
        <Rect x={0} y={0} width={CARD_WIDTH} height={height} fill={appColors.background.app} />
        <Rect
          x={PADDING / 2}
          y={PADDING / 2}
          width={CARD_WIDTH - PADDING}
          height={height - PADDING}
          rx={36}
          fill={appColors.background.panel}
          stroke={appColors.border.strong}
          strokeWidth={2}
        />

        <SvgText
          x={PADDING + 24}
          y={(y += 44)}
          fill={appColors.accent.primary}
          fontFamily={FONT}
          fontSize={26}
          fontWeight="700"
          letterSpacing={8}
        >
          {/* eslint-disable-next-line i18n/no-literal-text -- the product wordmark is not translated */}
          {'GOFFY'}
        </SvgText>
        <SvgText
          x={PADDING + 24}
          y={(y += 62)}
          fill={appColors.text.primary}
          fontFamily={FONT}
          fontSize={52}
          fontWeight="800"
        >
          {ellipsize(content.title, 52, CARD_WIDTH - 2 * PADDING - 48)}
        </SvgText>
        {content.subtitle
          ? mutedLine(ellipsize(content.subtitle, 30, CARD_WIDTH - 2 * PADDING - 48), 30, 'start', PADDING + 24, (y += 44))
          : null}

        <G>
          {mutedLine('#', 22, 'middle', PADDING + 46, (y += 58))}
          {mutedLine(content.valueLabel.toUpperCase(), 22, 'end', valueX, y)}
        </G>

        {layout.standings.map((row, index) => {
          const top = y + ROW_GAP;
          y = top + ROW_HEIGHT;
          const medal = MEDAL_COLORS[index];
          const centerY = top + ROW_HEIGHT / 2;
          return (
            <G key={row.id}>
              <Rect
                x={PADDING}
                y={top}
                width={CARD_WIDTH - 2 * PADDING}
                height={ROW_HEIGHT}
                rx={18}
                fill={index === 0 ? appColors.accent.subtleBg : appColors.surface.alt}
                stroke={medal ?? appColors.border.strong}
                strokeWidth={medal ? 2 : 1}
              />
              <SvgText
                x={PADDING + 46}
                y={centerY + 12}
                fill={medal ?? appColors.text.muted}
                fontFamily={FONT}
                fontSize={34}
                fontWeight="800"
                textAnchor="middle"
              >
                {index + 1}
              </SvgText>
              <SvgText
                x={nameX}
                y={centerY + 12}
                fill={appColors.text.primary}
                fontFamily={FONT}
                fontSize={34}
                fontWeight="700"
              >
                {ellipsize(row.name, 34, nameMaxWidth)}
              </SvgText>
              <SvgText
                x={valueX}
                y={centerY + 14}
                fill={index === 0 ? appColors.accent.primary : appColors.text.primary}
                fontFamily={FONT}
                fontSize={40}
                fontWeight="800"
                textAnchor="end"
              >
                {row.value}
              </SvgText>
            </G>
          );
        })}

        {layout.standingsMore > 0
          ? mutedLine(moreLabel(layout.standingsMore), 24, 'middle', CARD_WIDTH / 2, (y += MORE_HEIGHT - 8))
          : null}

        {layout.sections.map((section) => {
          y += SECTION_GAP;
          const titleY = (y += SECTION_TITLE_HEIGHT) - 14;
          return (
            <G key={section.id}>
              <SvgText
                x={PADDING + 24}
                y={titleY}
                fill={appColors.accent.primary}
                fontFamily={FONT}
                fontSize={26}
                fontWeight="700"
                letterSpacing={3}
              >
                {ellipsize(section.title.toUpperCase(), 26, CARD_WIDTH - 2 * PADDING - 48)}
              </SvgText>
              {section.lines.map((line) => {
                const lineY = (y += LINE_HEIGHT) - 10;
                return (
                  <G key={line.id}>
                    <SvgText
                      x={PADDING + 24}
                      y={lineY}
                      fill={appColors.text.secondary}
                      fontFamily={FONT}
                      fontSize={28}
                      fontWeight="600"
                    >
                      {ellipsize(line.label, 28, labelMaxWidth)}
                    </SvgText>
                    <SvgText
                      x={valueX}
                      y={lineY}
                      fill={appColors.text.primary}
                      fontFamily={FONT}
                      fontSize={28}
                      fontWeight="700"
                      textAnchor="end"
                    >
                      {ellipsize(line.value, 28, valueMaxWidth)}
                    </SvgText>
                  </G>
                );
              })}
              {section.more > 0
                ? mutedLine(moreLabel(section.more), 22, 'start', PADDING + 24, (y += MORE_HEIGHT) - 12)
                : null}
            </G>
          );
        })}

        {layout.sectionsMore > 0
          ? mutedLine(moreLabel(layout.sectionsMore), 22, 'middle', CARD_WIDTH / 2, height - PADDING - FOOTER_HEIGHT + 20)
          : null}
        {content.footer
          ? mutedLine(ellipsize(content.footer, 24, CARD_WIDTH - 2 * PADDING), 24, 'middle', CARD_WIDTH / 2, height - PADDING - 8)
          : null}
      </Svg>
    );
  }
);

export default ShareLeaderboardCard;
